import Fingerprint2 from 'fingerprintjs2';
import { setByDot } from 'feathers-hooks-common';
import {
  fileTypes,
  GHOST_MODE_TYPE_MERCHANT,
  PAYMENT_METHOD_TYPE_PAYPAL,
  PAYMENT_STATUS_AUTHORIZED,
  PAYMENT_STATUS_CAPTURED,
  PAYMENT_STATUS_PARTIALLY_REFUNDED,
  PAYMENT_STATUS_SETTLED,
  TRANSACTION_TYPE_REFUND,
  TXN_SOURCE_PLATFORM
} from '../constants';

const refEnvMap = {
  sandbox: process.env.SBX_WEB_SDK_ENV,
  live: process.env.WEB_SDK_ENV
};

export const isImage = (url) => {
  let newUrl = url;
  if (Array.isArray(url)) newUrl = '';
  const arrInfo = (newUrl || '').split('?');
  return arrInfo.shift().match(/\.(jpg|jpeg|png|gif)$/) !== null;
};

export const getNameFromUrl = (url) => {
  let newUrl = url;
  if (Array.isArray(url)) newUrl = '';
  return decodeURIComponent(
    ((newUrl || '').split('/').pop() || '').split('?').shift() || ''
  );
};

export const getSDKEnvironment = ({ isSandBox = false }) =>
  isSandBox ? refEnvMap.sandbox : refEnvMap.live;

export const getSDKClientOptions = ({ isSandBox = false, authorization }) => {
  const sdkEnvironment = getSDKEnvironment({ isSandBox });

  if (!sdkEnvironment && !process.env.PAN_VAULT_URL) {
    throw new Error('SDK environment and tokenizer URL is undefined');
  }

  if (!sdkEnvironment && !process.env.MAIN_API_URL) {
    throw new Error('SDK environment and seamlesspayApi URL is undefined');
  }
  const protocol = process.env.NODE_ENV === 'development' ? 'http' : 'https';
  return sdkEnvironment
    ? { environment: sdkEnvironment, accessToken: authorization }
    : {
        tokenizerApi: { url: `${protocol}://${process.env.PAN_VAULT_URL}` },
        seamlesspayApi: {
          url: `${protocol}://${process.env.MAIN_API_URL}`,
          accessToken: authorization
        }
      };
};

export const getHostedFieldValidateStatus = ({
  isEmpty,
  isValid,
  required,
  disabled,
  isTokenized
}) => {
  const truthMap = {
    success: [
      [disabled], // OR
      [isTokenized], // OR
      [isEmpty, !required], // OR
      [isValid]
    ],
    error: [
      [!disabled, !isTokenized, required, isEmpty], // OR
      [!disabled, !isTokenized, !isEmpty, !isValid]
    ]
  };

  const orderedStatuses = ['error', 'success'];
  const statuses = orderedStatuses.reduce(
    (result, status) => ({
      ...result,
      [status]: truthMap[status].some((conditions) =>
        conditions.every((isTrue) => isTrue)
      )
    }),
    {}
  );

  return Object.keys(statuses).find((status) => statuses[status]);
};

export const getDeviceFingerprint = () => {
  return new Promise((resolve, reject) => {
    try {
      new Fingerprint2().get((fingerprint, components) =>
        resolve({ fingerprint, components })
      );
    } catch (error) {
      reject(error);
    }
  });
};

export const getFileTypeByName = (name = '') => {
  const ext = name.split('.').pop();
  return fileTypes[ext] || ext;
};

export const prepareFile = ({ name, arrayBuffer }) => {
  const size = arrayBuffer.byteLength;
  const status = 'done';
  const type = getFileTypeByName(name);
  const uid = `rc-upload-${Date.now()}`;
  const originFileObj = new File([arrayBuffer], name, { type });
  return {
    name,
    originFileObj,
    size,
    status,
    type,
    uid
  };
};

export const createBufferFiles = (urls = []) => {
  const prepareFileBySource = async ({ name, response }) => {
    const arrayBuffer = await response.arrayBuffer();
    prepareFile({ name, arrayBuffer });
  };
  const createFile = ({ name, url }) =>
    fetch(url).then((response) => prepareFileBySource({ name, response }));
  return Promise.all(urls.map(createFile));
};

export const decodeHtmlEntity = (str = '') =>
  str.replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec));

export const setQuery = ({ name, props = {}, query }) => {
  setByDot(props, 'global.request.name', name);
  setByDot(props, 'global.request.query', query);
  return props;
};

export const isPermittedForVoid = ({ status, isRefunded, source, tranCode }) =>
  [PAYMENT_STATUS_AUTHORIZED, PAYMENT_STATUS_CAPTURED].includes(status) &&
  !isRefunded &&
  source !== TXN_SOURCE_PLATFORM &&
  tranCode !== TRANSACTION_TYPE_REFUND;

export const isPermittedForCapture = ({ status, isRefunded, paymentMethodType }) =>
  [PAYMENT_STATUS_AUTHORIZED].includes(status) &&
  !isRefunded &&
  paymentMethodType !== PAYMENT_METHOD_TYPE_PAYPAL;

export const isPermittedForRefund = ({ status, isRefunded, tranCode, source }) =>
  [PAYMENT_STATUS_SETTLED, PAYMENT_STATUS_PARTIALLY_REFUNDED].includes(status) &&
  !isRefunded &&
  tranCode !== TRANSACTION_TYPE_REFUND &&
  source !== TXN_SOURCE_PLATFORM;

export const getIsSandBox = ({ editor, global = { data: {} }, testMode = null }) => {
  const { isGhostTestMode } = editor;
  const ghost = global.data.ghost || {};
  const isGhostMerchant = ghost && ghost.type === GHOST_MODE_TYPE_MERCHANT;
  const selectMode = [
    {
      condition: isGhostMerchant,
      value: isGhostTestMode
    },
    {
      condition: testMode !== null,
      value: testMode
    },
    {
      condition: true,
      value: ((global.data.user || {}).data || {}).testmode
    }
  ];
  const { value } = selectMode.find(({ condition = false }) => condition);

  return Boolean(value);
};

export const formatCurrency = (value) =>
  value.indexOf('-') === -1 ? value : `(${String(value).replace('- ', '')})`;

export const formatRefundCurrency = (value, tranCode) =>
  tranCode !== TRANSACTION_TYPE_REFUND ? value : `(${value})`;
export const formatVerifyCurrency = (amount) => `(${amount})`;

export default {
  formatVerifyCurrency,
  isImage,
  getNameFromUrl,
  getSDKEnvironment,
  getHostedFieldValidateStatus,
  getDeviceFingerprint,
  createBufferFiles,
  getFileTypeByName,
  decodeHtmlEntity,
  setQuery,
  isPermittedForVoid,
  isPermittedForCapture,
  isPermittedForRefund,
  getIsSandBox,
  formatCurrency,
  formatRefundCurrency
};
